import { produce } from "immer";
import React, { ComponentProps } from "react";
import {
  FilterName,
  FilterState,
} from "../../adapters/insights-agent/state/AppState.ts";
import { contextualRelevancyMapping } from "../../adapters/insights-agent/state/contextualRelevancyMapping.ts";
import { Dropdown } from "../../ui-library/Dropdown/Dropdown.tsx";
import { InputLabel } from "../../ui-library/InputLabel/InputLabel.tsx";
import { MonthRangePicker } from "../../ui-library/MonthRangePicker/MonthRangePicker.tsx";
import styles from "./FilterDialog.module.css";

type DropdownItem = ComponentProps<typeof Dropdown>["items"][number];

const column1: {
  name: string;
  slug: "contextual_relevancy" | "country" | "content_type" | "keywords";
}[] = [
  { name: "Search Relevancy", slug: "contextual_relevancy" },
  { name: "Country", slug: "country" },
  { name: "Data Type", slug: "content_type" },
  { name: "Keywords", slug: "keywords" },
];

const column2: {
  name: string;
  slug:
    | "file_extension"
    | "language"
    | "business_areas_of_focus"
    | "business_sector"
    | "research_methodology";
}[] = [
  { name: "Document Type", slug: "file_extension" },
  { name: "Language", slug: "language" },
  { name: "Business Questions", slug: "business_areas_of_focus" },
  { name: "Sector", slug: "business_sector" },
  { name: "Methodology", slug: "research_methodology" },
];

export type LegacyFilterOptionsProps = {
  filterOptions?: Partial<Record<FilterName, { value: string[] }>>;
  filterState: FilterState;
  onFilterChange: (filterState: FilterState) => void;
  loading?: boolean;
};

function useMappedFilterOptions(
  filterOptions: LegacyFilterOptionsProps["filterOptions"],
) {
  return React.useMemo(() => {
    if (!filterOptions) return undefined;

    const mapped: Partial<
      Omit<Record<FilterName, DropdownItem[]>, "creation_date">
    > = {};

    Object.entries(filterOptions).forEach(([key, value]) => {
      if (!Array.isArray(value.value)) return;
      // @ts-expect-error This is safe because we are iterating over the keys of filterOptions
      mapped[key] = value.value.map((option) => ({
        label:
          key === "contextual_relevancy"
            ? contextualRelevancyMapping[option]
            : option,
        value: option,
      }));
    });

    return mapped;
  }, [filterOptions]);
}

export const LegacyFilterOptions: React.FC<LegacyFilterOptionsProps> = ({
  filterOptions,
  filterState,
  onFilterChange,
}) => {
  const mappedFilterOptions = useMappedFilterOptions(filterOptions);
  return (
    <div className={styles.Content}>
      <div>
        <div>
          <MonthRangePicker
            label="Creation date"
            value={filterState.creation_date || {}}
            onSelect={(value) => {
              onFilterChange(
                produce(filterState, (draft) => {
                  draft.creation_date = value;
                }),
              );
            }}
          />
        </div>
        {column1.map((item) => (
          <FilterOption
            key={item.slug}
            slug={item.slug}
            label={item.name}
            options={mappedFilterOptions?.[item.slug] || []}
            selected={filterState[item.slug] || []}
            onSelect={(value) => {
              onFilterChange(
                produce(filterState, (draft) => {
                  draft[item.slug] = value;
                }),
              );
            }}
            searchable={item.slug !== "contextual_relevancy"}
            sorting={
              item.slug !== "contextual_relevancy" ? "alphabetical" : undefined
            }
          />
        ))}
      </div>
      <div>
        {column2.map((item) => (
          <FilterOption
            key={item.slug}
            slug={item.slug}
            label={item.name}
            options={mappedFilterOptions?.[item.slug] || []}
            selected={filterState[item.slug] || []}
            onSelect={(value) =>
              onFilterChange(
                produce(filterState, (draft) => {
                  draft[item.slug] = value;
                }),
              )
            }
            searchable
            sorting={"alphabetical"}
          />
        ))}
      </div>
    </div>
  );
};

type DropdownProps = ComponentProps<typeof Dropdown>;
const FilterOption: React.FC<{
  label: string;
  slug: string;
  options: DropdownItem[];
  selected: DropdownProps["selected"];
  onSelect: DropdownProps["onSelect"];
  searchable: DropdownProps["searchable"];
  sorting?: DropdownProps["sorting"];
}> = (props) => {
  return (
    <div>
      <InputLabel for={props.slug}>{props.label}</InputLabel>
      <Dropdown
        id={props.slug}
        placeholder={"All"}
        items={props.options}
        onSelect={props.onSelect}
        selected={props.selected}
        multiple
        searchable={props.searchable}
        sorting={props.sorting}
      />
    </div>
  );
};
